/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CDS.DATASET.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/CdsDatasetListPage.tsx (the CDS
 * "Content Dataset Service" dataset registry). A DISTINCT presentation (dense
 * card list + facet chips + bottom-sheet detail + edit sheet) over the SAME
 * data/logic layer only:
 *   - reads via apiDb from cds_dataset (same columns the desktop list selects),
 *   - the header write (name / scope / flags / active) routes through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 *
 * source_kind / scope_mode are CHECK-enums, so their labels go through t()
 * labelled constants (§17.B11) — never the stored literal — and the Y/N flags
 * render as status pills (§17.B10). It never re-renders the desktop grid; §16.
 *
 * The full dataset builder — source-column reflection (cds_source_columns),
 * filter params, and the live cds_run preview — stays on the desktop; mobile
 * edits the reusable header flags only, mirroring MobileUserEditSheet /
 * MobileOrgEditSheet (structural editing stays on desktop).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, SquarePen, Database } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { CdsSourceKind, CdsScopeMode, YN } from '../../types/cds';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CDS.DATASET.LIST';

interface MobileDatasetRow {
  dataset_id: string;
  internal_code: string;
  name: string;
  source_kind: CdsSourceKind;
  source_ref: string;
  default_order: string | null;
  scope_mode: CdsScopeMode;
  public_anon: YN;
  can_write: YN;
  is_active: YN;
  updated_at: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labels — defined once as labelled constants (§17.B11); the stored
// value stays the code, only the displayed label is localised via t().
const SOURCE_KINDS: { value: CdsSourceKind; labelKey: string; fallback: string }[] = [
  { value: 'DDV', labelKey: 'mobile.cds.kind.DDV', fallback: 'Data view' },
  { value: 'VIEW', labelKey: 'mobile.cds.kind.VIEW', fallback: 'SQL view' },
  { value: 'RPC', labelKey: 'mobile.cds.kind.RPC', fallback: 'Function' },
];

const SCOPE_MODES: { value: CdsScopeMode; labelKey: string; fallback: string }[] = [
  { value: 'COMPANY_ONLY', labelKey: 'mobile.cds.scope.COMPANY_ONLY', fallback: 'Company only' },
  { value: 'SHARED', labelKey: 'mobile.cds.scope.SHARED', fallback: 'Shared' },
  { value: 'CROSS_PARTY', labelKey: 'mobile.cds.scope.CROSS_PARTY', fallback: 'Cross-party' },
];

export function MobileCdsDatasetListPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<MobileDatasetRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [kindFacet, setKindFacet] = useState<CdsSourceKind[]>([]);
  const [selected, setSelected] = useState<MobileDatasetRow | null>(null);
  const [editing, setEditing] = useState<MobileDatasetRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindLabel = (v: CdsSourceKind): string => {
    const opt = SOURCE_KINDS.find((o) => o.value === v);
    return opt ? t(opt.labelKey, opt.fallback) : v;
  };
  const scopeLabel = (v: CdsScopeMode): string => {
    const opt = SCOPE_MODES.find((o) => o.value === v);
    return opt ? t(opt.labelKey, opt.fallback) : v;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('cds_dataset')
        .select('dataset_id, internal_code, name, source_kind, source_ref, default_order, public_anon, can_write, scope_mode, is_active, updated_at')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as MobileDatasetRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load once on mount. cds_dataset.name is a plain column (no _tl), so there is
  // no active-locale dependency to re-run on.
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (kindFacet.length && !kindFacet.includes(r.source_kind)) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.internal_code.toLowerCase().includes(q) ||
        r.source_ref.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, kindFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.cds.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleKind = (v: CdsSourceKind) =>
    setKindFacet((prev) => (prev.includes(v) ? prev.filter((k) => k !== v) : [...prev, v]));

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.cds.title', 'Content Datasets')}
        description={t('mobile.cds.description', 'Reusable data views that feed content pages and dynamic grids.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.cds.search', 'Search datasets')}
          aria-label={t('mobile.cds.search', 'Search datasets')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={SOURCE_KINDS.map((o) => ({ value: o.value, label: kindLabel(o.value) }))}
        isActive={(v) => kindFacet.includes(v as CdsSourceKind)}
        onSelect={(v) => toggleKind(v as CdsSourceKind)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.cds.none', 'No datasets match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.dataset_id}
              lead={<Database />}
              leadTone={r.public_anon === 'Y' ? 'amber' : 'blue'}
              title={r.name}
              subtitle={`${r.internal_code} · ${kindLabel(r.source_kind)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      <MobileDatasetDetailSheet
        row={selected}
        kindLabel={kindLabel}
        scopeLabel={scopeLabel}
        onClose={() => setSelected(null)}
        onEdit={(r) => { setEditing(r); setSelected(null); }}
      />

      <MobileDatasetEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Read-only detail bottom-sheet.
// ---------------------------------------------------------------------------

interface MobileDatasetDetailSheetProps {
  row: MobileDatasetRow | null;
  kindLabel: (v: CdsSourceKind) => string;
  scopeLabel: (v: CdsScopeMode) => string;
  onClose: () => void;
  onEdit: (row: MobileDatasetRow) => void;
}

function MobileDatasetDetailSheet({ row, kindLabel, scopeLabel, onClose, onEdit }: MobileDatasetDetailSheetProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={onClose} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.cds.detail', 'Dataset detail')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{row.name}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.cds.status', 'Status')}
                value={
                  <MobileStatusPill tone={row.is_active === 'Y' ? 'ok' : 'no'}>
                    {row.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.cds.source', 'Source')} value={`${kindLabel(row.source_kind)} · ${row.source_ref}`} />
              <MobileDetailRow label={t('mobile.cds.scope_lbl', 'Scope')} value={scopeLabel(row.scope_mode)} />
              <MobileDetailRow label={t('mobile.cds.order', 'Default order')} value={row.default_order || t('common.none', 'None')} />
              <MobileDetailRow
                label={t('mobile.cds.public', 'Public (anon)')}
                value={
                  <MobileStatusPill tone={row.public_anon === 'Y' ? 'ok' : 'no'}>
                    {row.public_anon === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.cds.can_write', 'Inline edit')}
                value={
                  <MobileStatusPill tone={row.can_write === 'Y' ? 'ok' : 'no'}>
                    {row.can_write === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.cds.updated', 'Last updated')} value={formatDateTime(row.updated_at)} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" onClick={() => onEdit(row)}>
                <SquarePen size={16} />
                {t('mobile.cds.edit', 'Edit')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" onClick={onClose} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the reusable dataset header (name, scope, flags), saved through
// the Common Routine (routedUpdate) — §2. Source kind/ref and the column/param
// builder stay on the desktop, so the source is shown read-only here (mirrors
// MobileUserEditSheet's read-only roles).
// ---------------------------------------------------------------------------

interface MobileDatasetEditSheetProps {
  session: AuthSession;
  row: MobileDatasetRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileDatasetEditSheet({ session, row, onClose, onSaved }: MobileDatasetEditSheetProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<CdsScopeMode>('COMPANY_ONLY');
  const [defaultOrder, setDefaultOrder] = useState('');
  const [publicAnon, setPublicAnon] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different dataset is opened.
  useEffect(() => {
    if (!row) return;
    setName(row.name);
    setScope(row.scope_mode);
    setDefaultOrder(row.default_order ?? '');
    setPublicAnon(row.public_anon === 'Y');
    setCanWrite(row.can_write === 'Y');
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    if (!name.trim()) { setError(t('mobile.cds.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'cds_dataset', 'dataset_id', row.dataset_id, {
      name: name.trim(),
      scope_mode: scope,
      default_order: defaultOrder.trim() || null,
      public_anon: publicAnon ? 'Y' : 'N',
      can_write: canWrite ? 'Y' : 'N',
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.cds.edit', 'Edit dataset')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.cds.edit', 'Edit dataset')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-cds-name">{t('mobile.cds.name', 'Name')}</label>
                <input
                  id="m-cds-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-cds-scope">{t('mobile.cds.scope_lbl', 'Scope')}</label>
                <select id="m-cds-scope" className="m-sel" value={scope} onChange={(e) => setScope(e.target.value as CdsScopeMode)}>
                  {SCOPE_MODES.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-cds-order">{t('mobile.cds.order', 'Default order')}</label>
                <input
                  id="m-cds-order"
                  className="m-inp"
                  value={defaultOrder}
                  onChange={(e) => setDefaultOrder(e.target.value)}
                  placeholder={t('mobile.cds.order_ph', 'e.g. name asc')}
                />
              </div>
              <div className="m-formrow">
                <label>{t('mobile.cds.source', 'Source')}</label>
                <div className="m-formrow-ro">
                  {row.source_kind} · {row.source_ref}
                  <span className="m-formrow-hint"> · {t('mobile.cds.source_desktop', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.cds.public', 'Public (anon)')}</div>
                  <div className="m-formrow-s">{t('mobile.cds.public_hint', 'Readable without signing in')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={publicAnon}
                  className={'m-switch ' + (publicAnon ? 'm-on' : 'm-off')}
                  onClick={() => setPublicAnon((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.cds.can_write', 'Inline edit')}</div>
                  <div className="m-formrow-s">{t('mobile.cds.can_write_hint', 'Allow editing rows in place')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={canWrite}
                  className={'m-switch ' + (canWrite ? 'm-on' : 'm-off')}
                  onClick={() => setCanWrite((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.cds.active_hint', 'Available to place on pages')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
